import React, { useMemo, useState } from 'react';
import { useAppStore } from '../../../../hooks/useAppStore';
import { PageLayout } from '../shared/PageLayout';
import { DataTable } from '../shared/DataTable';
import { SlideOver } from '../shared/SlideOver';
import { ConnectionBadge } from '../shared/ConnectionBadge';
import { StatCard } from '../shared/StatCard';
import { Avatar } from '../shared/Avatar';
import { MasteryBar } from '../shared/MasteryBar';
import { timeAgo, SUBJECT_LABELS } from '../shared/utils';
import { SearchIcon, UsersIcon, SchoolIcon, TrendingUpIcon, ClockIcon, BookOpenIcon, FilterIcon, ChevronDownIcon, Plus } from 'lucide-react';
import { Button } from '../../../ui/Button';
import { isTeacherAccount } from '../../../../types/accounts';
export function LearnersPage() {
  const {
    accounts,
    createAccount,
    getLearnerData
  } = useAppStore();
  // accounts is AccountsState (object) — extract flat array
  const allAccounts = useMemo(() => accounts?.accountsById ? Object.values(accounts.accountsById) : [], [accounts]);
  const [search, setSearch] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('');
  const [selectedLearnerId, setSelectedLearnerId] = useState<string | null>(null);
  // Add Modal State
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newLearnerName, setNewLearnerName] = useState('');
  const [newLearnerGrade, setNewLearnerGrade] = useState('6');
  const handleAddLearner = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newLearnerName.trim()) return;
    createAccount('learner', {
      displayName: newLearnerName,
      grade: parseInt(newLearnerGrade, 10)
    });
    setIsAddModalOpen(false);
    setNewLearnerName('');
  };
  const learners = useMemo(() => {
    return allAccounts.filter(a => a.role === 'learner');
  }, [allAccounts]);
  const enrichedLearners = useMemo(() => {
    return learners.map(learner => {
      // Derive school name from teacher relationships
      let schoolName = (learner as any).schoolName;
      if (!schoolName) {
        const teacherId = Object.entries(accounts?.relationships?.teacherToLearners || {}).find(([_, ids]) => ids.includes(learner.id))?.[0];
        if (teacherId && accounts?.accountsById) {
          const teacher = accounts.accountsById[teacherId];
          if (teacher && isTeacherAccount(teacher)) {
            schoolName = teacher.schoolName;
          }
        }
      }
      const learnerData = getLearnerData(learner.id);
      const familyMastery = learnerData?.familyMastery || {};
      const masteryValues = Object.values(familyMastery).map(m => m.masteryScore);
      const avgMastery = masteryValues.length > 0 ? Math.round(masteryValues.reduce((sum, val) => sum + val, 0) / masteryValues.length) : 0;
      // Proxy session count from total attempts
      const totalAttempts = Object.values(familyMastery).reduce((sum, m) => sum + m.attempts, 0);
      const sessionCount = Math.ceil(totalAttempts / 10);
      return {
        ...learner,
        schoolName,
        sessionCount,
        avgMastery,
        lastActive: learner.lastActive ?? 0,
        familyMastery
      };
    });
  }, [learners, getLearnerData, accounts]);
  const schools = useMemo(() => {
    const s = new Set(enrichedLearners.map(l => l.schoolName).filter(Boolean) as string[]);
    return Array.from(s).sort();
  }, [enrichedLearners]);
  const filtered = useMemo(() => {
    return enrichedLearners.filter(l => {
      const matchSearch = !search || l.displayName.toLowerCase().includes(search.toLowerCase()) || (l.schoolName ?? '').toLowerCase().includes(search.toLowerCase());
      const matchSchool = !schoolFilter || l.schoolName === schoolFilter;
      return matchSearch && matchSchool;
    });
  }, [enrichedLearners, search, schoolFilter]);
  const selectedLearner = useMemo(() => selectedLearnerId ? enrichedLearners.find(l => l.id === selectedLearnerId) : null, [selectedLearnerId, enrichedLearners]);
  const selectedMastery = useMemo(() => {
    if (!selectedLearner) return [];
    const familyMastery = selectedLearner.familyMastery || {};
    const bySubject: Record<string, number[]> = {};
    Object.values(familyMastery).forEach(m => {
      const subjectId = m.familyId.split('-')[0] ?? 'UNK';
      if (!bySubject[subjectId]) bySubject[subjectId] = [];
      bySubject[subjectId].push(m.masteryScore);
    });
    return Object.entries(bySubject).map(([subjectId, values]) => ({
      subjectId,
      label: SUBJECT_LABELS[subjectId] ?? subjectId,
      avg: Math.round(values.reduce((a, b) => a + b, 0) / values.length),
      count: values.length
    })).sort((a, b) => b.avg - a.avg);
  }, [selectedLearner]);
  const avgMastery = useMemo(() => {
    if (enrichedLearners.length === 0) return 0;
    return Math.round(enrichedLearners.reduce((sum, l) => sum + l.avgMastery, 0) / enrichedLearners.length);
  }, [enrichedLearners]);
  const totalSessions = useMemo(() => {
    return enrichedLearners.reduce((sum, l) => sum + l.sessionCount, 0);
  }, [enrichedLearners]);
  const columns = [{
    key: 'displayName',
    label: 'Naam',
    render: (v: string, row: (typeof filtered)[0]) => <div className="flex items-center gap-2.5" data-id="element-834">
          <Avatar name={v} data-id="element-835" />
          <div data-id="element-836">
            <div className="text-sm font-medium text-gray-900" data-id="element-837">{v}</div>
            <div className="text-xs text-gray-400" data-id="element-838">{row.groupName ?? '—'}</div>
          </div>
        </div>
  }, {
    key: 'schoolName',
    label: 'School',
    render: (v: string) => <span className="text-sm text-gray-600" data-id="element-839">{v ?? '—'}</span>
  }, {
    key: 'sessionCount',
    label: 'Sessies',
    render: (v: number) => <span className="text-sm font-medium text-gray-700 tabular-nums" data-id="element-840">
          {v}
        </span>
  }, {
    key: 'avgMastery',
    label: 'Beheersing',
    render: (v: number) => <MasteryBar value={v} data-id="element-841" />
  }, {
    key: 'lastActive',
    label: 'Laatste activiteit',
    render: (v: number) => <span className="text-xs text-gray-500" data-id="element-842">{timeAgo(v)}</span>
  }];
  return <PageLayout title="Leerlingen" description={`${filtered.length} van ${learners.length} leerlingen`} actions={<div className="flex gap-2" data-id="element-844">
          <ConnectionBadge status="live" data-id="element-845" />
          <Button size="sm" onClick={() => setIsAddModalOpen(true)} data-id="element-846">
            <Plus className="w-4 h-4 mr-1.5" data-id="element-847" />
            Leerling toevoegen
          </Button>
        </div>} data-id="element-843">
      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6" data-id="element-848">
        <StatCard label="Totaal leerlingen" value={learners.length} icon={<UsersIcon className="w-5 h-5" data-id="element-850" />} color="blue" data-id="element-849" />
        <StatCard label="Scholen" value={schools.length} icon={<SchoolIcon className="w-5 h-5" data-id="element-852" />} color="purple" data-id="element-851" />
        <StatCard label="Gem. beheersing" value={`${avgMastery}%`} icon={<TrendingUpIcon className="w-5 h-5" data-id="element-854" />} color="emerald" data-id="element-853" />
        <StatCard label="Totaal sessies" value={totalSessions} icon={<BookOpenIcon className="w-5 h-5" data-id="element-856" />} color="amber" data-id="element-855" />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4" data-id="element-857">
        <div className="relative flex-1" data-id="element-858">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-859" />
          <input type="text" placeholder="Zoek op naam of school..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-860" />
        </div>
        <div className="relative" data-id="element-861">
          <FilterIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-862" />
          <select value={schoolFilter} onChange={e => setSchoolFilter(e.target.value)} className="pl-9 pr-8 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 appearance-none bg-white" data-id="element-863">
            <option value="" data-id="element-864">Alle scholen</option>
            {schools.map(s => <option key={s} value={s} data-id="element-865">
                {s}
              </option>)}
          </select>
          <ChevronDownIcon className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" data-id="element-866" />
        </div>
      </div>

      {/* Table */}
      {filtered.length === 0 ? <div className="bg-white rounded-xl border border-gray-200 py-16 text-center" data-id="element-867">
          <UsersIcon className="w-10 h-10 text-gray-200 mx-auto mb-3" data-id="element-868" />
          <p className="text-sm text-gray-500" data-id="element-869">Geen leerlingen gevonden</p>
          {(search || schoolFilter) && <button onClick={() => {
        setSearch('');
        setSchoolFilter('');
      }} className="mt-2 text-xs text-blue-600 hover:underline" data-id="element-870">
              Filters wissen
            </button>}
        </div> : <DataTable columns={columns} data={filtered} onRowClick={row => setSelectedLearnerId(row.id)} data-id="element-871" />}

      {/* Detail SlideOver */}
      <SlideOver open={!!selectedLearner} onClose={() => setSelectedLearnerId(null)} title={selectedLearner?.displayName ?? ''} data-id="element-872">
        {selectedLearner && <div className="space-y-6" data-id="element-873">
            {/* Header */}
            <div className="flex items-center gap-4 pb-4 border-b border-gray-100" data-id="element-874">
              <Avatar name={selectedLearner.displayName} size="md" data-id="element-875" />
              <div data-id="element-876">
                <div className="font-semibold text-gray-900" data-id="element-877">
                  {selectedLearner.displayName}
                </div>
                <div className="text-sm text-gray-500" data-id="element-878">
                  {selectedLearner.schoolName ?? '—'} ·{' '}
                  {selectedLearner.groupName ?? '—'}
                </div>
                <div className="text-xs text-gray-400 mt-0.5" data-id="element-879">
                  Laatste activiteit: {timeAgo(selectedLearner.lastActive)}
                </div>
              </div>
            </div>

            {/* Quick stats */}
            <div className="grid grid-cols-2 gap-3" data-id="element-880">
              <div className="bg-blue-50 rounded-lg p-3" data-id="element-881">
                <div className="text-2xl font-bold text-blue-700" data-id="element-882">
                  {selectedLearner.sessionCount}
                </div>
                <div className="text-xs text-blue-600" data-id="element-883">Sessies</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-3" data-id="element-884">
                <div className="text-2xl font-bold text-emerald-700" data-id="element-885">
                  {selectedLearner.avgMastery}%
                </div>
                <div className="text-xs text-emerald-600" data-id="element-886">Gem. beheersing</div>
              </div>
            </div>

            {/* Mastery by subject */}
            {selectedMastery.length > 0 ? <div data-id="element-887">
                <h3 className="text-sm font-semibold text-gray-900 mb-3" data-id="element-888">
                  Beheersing per vak
                </h3>
                <div className="space-y-2.5" data-id="element-889">
                  {selectedMastery.map(s => <div key={s.subjectId} data-id="element-890">
                      <div className="flex justify-between text-xs mb-1" data-id="element-891">
                        <span className="text-gray-600" data-id="element-892">{s.label}</span>
                        <span className="text-gray-500" data-id="element-893">
                          {s.count} skills · {s.avg}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full overflow-hidden" data-id="element-894">
                        <div className={`h-2 rounded-full ${s.avg >= 80 ? 'bg-emerald-400' : s.avg >= 60 ? 'bg-amber-400' : 'bg-red-400'}`} style={{
                  width: `${s.avg}%`
                }} data-id="element-895" />
                      </div>
                    </div>)}
                </div>
              </div> : <div className="text-center py-8 bg-gray-50 rounded-xl" data-id="element-896">
                <BookOpenIcon className="w-8 h-8 text-gray-300 mx-auto mb-2" data-id="element-897" />
                <p className="text-sm text-gray-500" data-id="element-898">Nog geen voortgang</p>
              </div>}
          </div>}
      </SlideOver>

      {/* Add Learner Modal */}
      <SlideOver open={isAddModalOpen} onClose={() => setIsAddModalOpen(false)} title="Nieuwe leerling" description="Voeg een leerlingaccount toe" data-id="element-899">
        <form onSubmit={handleAddLearner} className="space-y-4" data-id="element-900">
          <div data-id="element-901">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-902">
              Naam
            </label>
            <input type="text" required value={newLearnerName} onChange={e => setNewLearnerName(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" placeholder="Voornaam of volledige naam" data-id="element-903" />
          </div>

          <div data-id="element-904">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-905">
              Groep
            </label>
            <select value={newLearnerGrade} onChange={e => setNewLearnerGrade(e.target.value)} className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-906">
              <option value="6" data-id="element-907">Groep 6</option>
              <option value="7" data-id="element-908">Groep 7</option>
              <option value="8" data-id="element-909">Groep 8</option>
            </select>
          </div>

          <div className="pt-4 flex justify-end gap-2" data-id="element-910">
            <Button type="button" variant="secondary" onClick={() => setIsAddModalOpen(false)} data-id="element-911">
              Annuleren
            </Button>
            <Button type="submit" data-id="element-912">Aanmaken</Button>
          </div>
        </form>
      </SlideOver>
    </PageLayout>;
}